import { DocumentStore } from '@/features/docs/DocumentStore'
import { DocsApi } from '@/features/docs/api/docs-api'
import Env from '@/infra/env'
import { DocumentTypeStore } from '@/features/docs/DocumentTypeStore'
import { RenameDocumentDialogViewModel } from '@/features/docs/RenameDocumentDialog/RenameDocumentDialogViewModel'
import { DeleteDocumentDialogViewModel } from '@/features/docs/DeleteDocumentDialog/DeleteDocumentDialogViewModel'
import { UnlinkDocumentDialogViewModel } from '@/features/docs/components/UnlinkDocumentDialog/UnlinkDocumentDialogViewModel'
import type {
  DocumentsFeatureOptions,
  PromptDeleteDocumentFn,
  PromptUnlinkDocumentFn,
  ShowRenameDocumentDialogFn,
} from '@/features/docs/index'
import {
  DocumentsListViewModel,
  type DocumentsListViewModelOptions,
} from '@/features/docs/DocumentsListViewModel'
import { DocumentsViewModel } from '@/features/docs/DocumentsView/DocumentsViewModel'
import {
  AssignFileTypeDialogViewModel,
  type PromptAssignFileTypesFn,
} from '@/features/docs/components/AssignFileTypeDialog/AssignFileTypeDialogViewModel'
import {
  DocumentUploaderViewModel,
  type DocumentUploaderViewModelOptions,
} from '@/features/docs/DocumentUploaderViewModel'
import {
  DocumentViewModel,
  type DocumentViewModelOptions,
} from '@/features/docs/DocumentViewModel'
import type { Document } from '@/features/docs/Document'
import type { DocumentLinkingViewModelOptions } from '@/features/docs/components/LinkDocumentsDialog/DocumentLinkingViewModel'
import { DocumentLinkingViewModel } from '@/features/docs/components/LinkDocumentsDialog/DocumentLinkingViewModel'

/**
 * Function for creating a document view model.
 */
export type InternalCreateDocumentViewModelFn = (
  document: Document,
  options: DocumentViewModelOptions,
) => DocumentViewModel

export function InternalDocumentsFeature(options: DocumentsFeatureOptions) {
  const api = new DocsApi({
    token$: options.token$,
    baseURL: Env.DOCUMENT_API,
  })

  const documentTypeStore = new DocumentTypeStore(api)
  const documentStore = new DocumentStore(
    options.token$,
    api,
    (id) => documentTypeStore.types.get(id) ?? null,
    options.getDocumentDownloadDelegate,
  )

  const documentRemoved$ = documentStore.documentRemoved$

  const renameDocumentDialogViewModel = new RenameDocumentDialogViewModel(
    options.createSnackbarErrorHandler,
    documentStore,
  )

  const deleteDocumentDialogViewModel = new DeleteDocumentDialogViewModel(
    options.createSnackbarErrorHandler,
    documentStore,
  )

  const promptDelete: PromptDeleteDocumentFn = (doc, signal) =>
    deleteDocumentDialogViewModel.prompt(doc, signal)

  const unlinkDocumentDialogViewModel = new UnlinkDocumentDialogViewModel(
    options.createSnackbarErrorHandler,
    documentStore,
    options.fetchJobGist,
    promptDelete,
  )

  const showRenameDialog: ShowRenameDocumentDialogFn = (doc, signal) =>
    renameDocumentDialogViewModel.show(doc, signal)

  const promptUnlink: PromptUnlinkDocumentFn = (doc, access, signal) =>
    unlinkDocumentDialogViewModel.prompt(doc, access, signal)

  const assignFileTypeDialogVm = new AssignFileTypeDialogViewModel(
    documentTypeStore,
  )

  const promptAssignFileTypes: PromptAssignFileTypesFn =
    assignFileTypeDialogVm.prompt.bind(assignFileTypeDialogVm)

  const createDocumentUploaderViewModel = (
    vmOptions: DocumentUploaderViewModelOptions,
  ) =>
    new DocumentUploaderViewModel(
      vmOptions,
      options.getCurrentWorkspace,
      options.newSnackbar,
      documentStore,
      promptAssignFileTypes,
    )

  const createDocumentViewModel: InternalCreateDocumentViewModelFn = (
    document,
    vmOptions,
  ) =>
    new DocumentViewModel(
      document,
      vmOptions,
      options.fetchUser,
      options.fetchJobGist,
      showRenameDialog,
      promptDelete,
      promptUnlink,
      documentStore,
      documentTypeStore,
    )

  const createDocumentsListViewModel = (
    vmOptions: DocumentsListViewModelOptions,
  ) => {
    const uploaderVm = createDocumentUploaderViewModel({
      access:
        vmOptions.context.type === 'Job'
          ? { type: 'Job', jobId: vmOptions.context.jobId }
          : null,
    })
    return new DocumentsListViewModel(
      vmOptions,
      options.getCurrentUser,
      options.getCurrentWorkspace,
      options.jobGistRealtime$,
      options.createSnackbarRetryWrapper,
      documentStore,
      createDocumentViewModel,
      uploaderVm,
    )
  }

  const createDocumentLinkingViewModel = (
    vmOptions: DocumentLinkingViewModelOptions,
  ) => {
    return new DocumentLinkingViewModel(
      vmOptions,
      options.createSnackbarErrorHandler,
      documentStore,
      createDocumentsListViewModel,
    )
  }

  // View model for the documents screen.
  const documentsVm = new DocumentsViewModel(
    createDocumentsListViewModel({
      context: { type: 'User' },
      fetchJobs: true,
      fetchAuthor: true,
    }),
  )

  return {
    documentStore,
    documentTypeStore,
    renameDocumentDialogViewModel,
    deleteDocumentDialogViewModel,
    unlinkDocumentDialogViewModel,
    documentRemoved$,
    showRenameDialog,
    promptDelete,
    promptUnlink,
    documentsVm,
    createDocumentViewModel,
    createDocumentsListViewModel,
    assignFileTypeDialogVm,
    createDocumentLinkingViewModel,
  }
}
